import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Heart, ShieldCheck, ChevronRight, ChevronLeft, Check, X,
  Sparkles, AlertTriangle, CalendarClock, Cigarette, Wine,
  Activity, Brain, Syringe, Search, Smile,
} from 'lucide-react';
import { API_URL as API } from '../../utils/api';

// Visual helpers ------------------------------------------------------------
const BG = 'bg-gradient-to-br from-sky-50 via-white to-teal-50/60';
const TILE = 'bg-white border-2 border-slate-200 hover:border-teal-400 rounded-3xl transition-all hover:shadow-lg';
const TILE_ACTIVE = 'bg-teal-500 border-2 border-teal-500 rounded-3xl shadow-lg';

// Small Emoji icon per section to feel friendly (no emoji in code elsewhere, but here for kids/elders UX)
const SECTION_EMOJI = {
  age: '🎂', motif: '💬', atcd: '📋', familial: '👨‍👩‍👧', traitement: '💊', allergie: '🌿',
  tabac: '🚬', alcool: '🍷', activite: '🏃', sommeil: '🌙', mental: '🧠', vaccin: '💉',
  depistage: '🔎', redflags: '🚨', consent: '🔒', done: '🎉', sex: '👤', antecedent: '📖',
};

// ---- Consent / eligibility screens ----------------------------------------
function Step_Welcome({ onStart }) {
  return (
    <div className="max-w-lg mx-auto text-center pt-12 px-4" data-testid="bilan-welcome">
      <div className="inline-block bg-gradient-to-br from-teal-400 to-sky-500 rounded-3xl p-6 mb-6 shadow-lg">
        <Heart className="w-10 h-10 text-white" strokeWidth={2.5} />
      </div>
      <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Bilan de santé
      </h1>
      <p className="text-slate-500 text-base mb-8">Quelques questions pour préparer votre consultation.</p>
      <button type="button"
        onClick={onStart}
        data-testid="bilan-start"
        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-sky-500 text-white rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
      >
        <Sparkles className="w-5 h-5" /> Commencer mon bilan
      </button>
      <p className="text-xs text-slate-400 mt-8 px-4">
        Vos réponses seront transmises à votre médecin. Elles seront automatiquement supprimées après 2h30.
      </p>
    </div>
  );
}

function Step_Consent({ onAccept, onReject }) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="max-w-lg mx-auto pt-6 px-4 pb-24" data-testid="bilan-consent">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-teal-100 rounded-2xl p-3"><ShieldCheck className="w-6 h-6 text-teal-700" /></div>
        <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Confidentialité de vos réponses</h2>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 text-sm text-slate-600 space-y-3 leading-relaxed">
        <p><strong className="text-slate-800">Finalité :</strong> préparer votre consultation avec votre médecin.</p>
        <p><strong className="text-slate-800">Destinataire :</strong> les soignants du centre.</p>
        <p><strong className="text-slate-800">Durée de conservation :</strong> 2h30 max.</p>
        <p><strong className="text-slate-800">Vos droits :</strong> vous pouvez demander à votre médecin la suppression immédiate de vos données.</p>
        <p className="italic text-slate-500">Base légale : consentement (art. 6.1.a et 9.2.a du RGPD).</p>
      </div>
      <label className="flex items-start gap-3 mt-6 cursor-pointer select-none" data-testid="bilan-consent-check">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-1 w-5 h-5 accent-teal-500" />
        <span className="text-sm text-slate-700">J'ai lu et j'accepte ces conditions pour transmettre mes réponses à mon médecin.</span>
      </label>
      <div className="mt-8 flex gap-3">
        <button type="button" onClick={onReject} data-testid="bilan-consent-reject" className="flex-1 py-3 border-2 border-slate-200 text-slate-500 rounded-full font-medium hover:bg-slate-50">
          Je refuse
        </button>
        <button type="button" onClick={onAccept} disabled={!checked} data-testid="bilan-consent-accept"
          className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-sky-500 text-white rounded-full font-semibold disabled:opacity-40 shadow-md">
          J'accepte <ChevronRight className="inline w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function Step_Rejected({ onBack }) {
  return (
    <div className="max-w-lg mx-auto pt-16 px-4 text-center" data-testid="bilan-rejected">
      <div className="inline-block bg-slate-100 rounded-3xl p-6 mb-4">
        <X className="w-10 h-10 text-slate-400" />
      </div>
      <h2 className="text-xl font-bold text-slate-700 mb-2">Aucun souci</h2>
      <p className="text-slate-500 mb-8 px-4">Vous pourrez en parler directement avec votre médecin en consultation.</p>
      <button type="button" onClick={onBack} className="px-6 py-3 bg-slate-200 text-slate-700 rounded-full">Retour</button>
    </div>
  );
}

// --- Step: age range -------------------------------------------------------
const AGE_RANGES = [
  { value: '18-25', label: '18 – 25 ans', active: true, emoji: '🌱' },
  { value: '45-50', label: '45 – 50 ans', active: true, emoji: '🌿' },
  { value: '60-65', label: '60 – 65 ans', active: true, emoji: '🌳' },
  { value: '70-75', label: '70 – 75 ans', active: true, emoji: '🌲' },
];
function Step_AgeRange({ onPick, onOut }) {
  return (
    <div className="max-w-lg mx-auto pt-6 px-4 pb-24" data-testid="bilan-age">
      <h2 className="text-xl font-bold text-slate-800 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{SECTION_EMOJI.age} Votre tranche d'âge</h2>
      <p className="text-sm text-slate-500 mb-6">Choisissez la tranche qui vous correspond.</p>
      <div className="grid grid-cols-2 gap-3">
        {AGE_RANGES.map((a) => (
          <button type="button" key={a.value} disabled={!a.active} onClick={() => onPick(a.value)}
            data-testid={`age-${a.value}`}
            className={`p-6 text-left ${a.active ? TILE : 'bg-slate-50 border-2 border-slate-100 rounded-3xl opacity-50'} ${!a.active ? 'cursor-not-allowed' : ''}`}>
            <div className="text-2xl mb-1">{a.emoji}</div>
            <div className="font-semibold text-slate-800">{a.label}</div>
            {!a.active && <div className="text-xs text-slate-400 mt-1">Bientôt disponible</div>}
          </button>
        ))}
      </div>
      <button type="button" onClick={onOut} data-testid="age-out" className="text-sm text-slate-500 underline mt-6">
        Je ne suis dans aucune de ces tranches
      </button>
    </div>
  );
}

function Step_OutOfRange({ onBack }) {
  return (
    <div className="max-w-lg mx-auto pt-16 px-4 text-center" data-testid="bilan-out">
      <div className="inline-block bg-amber-100 rounded-3xl p-6 mb-4">
        <CalendarClock className="w-10 h-10 text-amber-600" />
      </div>
      <h2 className="text-xl font-bold text-slate-700 mb-2">Bilan de Prévention</h2>
      <p className="text-slate-500 mb-8 px-4">Le Bilan Prévention de l'Assurance Maladie concerne les personnes de 18-25, 45-50, 60-65 et 70-75 ans. Parlez-en à votre médecin.</p>
      <button type="button" onClick={onBack} className="px-6 py-3 bg-slate-200 text-slate-700 rounded-full">Retour</button>
    </div>
  );
}

// --- Helpers for multi-choice buttons -------------------------------------
function Bool3Row({ options, value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2 mt-2">
      {options.map((o) => (
        <button type="button" key={o.value} onClick={() => onChange(o.value)}
          className={`py-3 rounded-2xl text-sm font-medium transition-all ${value === o.value ? TILE_ACTIVE + ' text-white' : TILE + ' text-slate-700'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
function MultiCheck({ options, value, onToggle, testidPrefix }) {
  return (
    <div className="space-y-2 mt-2">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button type="button" key={o} onClick={() => onToggle(o)}
            data-testid={`${testidPrefix}-${options.indexOf(o)}`}
            className={`w-full text-left p-4 rounded-2xl transition-all border-2 ${active ? 'bg-teal-500 border-teal-500 text-white shadow-md' : 'bg-white border-slate-200 text-slate-700 hover:border-teal-300'}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{o}</span>
              {active && <Check className="w-4 h-4 flex-shrink-0" />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// --- Big Step: already done in last 12 months ------------------------------
function Step_Already12m({ onAnswer, onBack }) {
  return (
    <div className="max-w-lg mx-auto pt-6 px-4 pb-24" data-testid="bilan-already">
      <h2 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Avez-vous déjà fait un bilan de santé au cours des 12 derniers mois ?
      </h2>
      <p className="text-sm text-slate-500 mb-6">Le Bilan Prévention est remboursé une fois par tranche d'âge.</p>
      <div className="space-y-3">
        {[
          { v: 'no', label: 'Non', color: 'teal', hint: 'Parfait, on continue !' },
          { v: 'nsp', label: 'Je ne sais pas', color: 'sky', hint: '' },
          { v: 'yes', label: 'Oui', color: 'amber', hint: "Inutile d'en refaire un aujourd'hui." },
        ].map((o) => (
          <button type="button" key={o.v} onClick={() => onAnswer(o.v)} data-testid={`already-${o.v}`}
            className={`w-full p-5 rounded-3xl text-left transition-all bg-white border-2 border-slate-200 hover:border-${o.color}-400 hover:shadow-md`}>
            <div className="font-semibold text-slate-800 text-base">{o.label}</div>
            {o.hint && <div className={`text-xs text-${o.color}-600 mt-1`}>{o.hint}</div>}
          </button>
        ))}
      </div>
      {onBack && (
        <div className="mt-8 text-center">
          <button type="button" onClick={onBack}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white border-2 border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50 text-sm font-medium transition-colors"
            data-testid="already-back">
            <ChevronLeft className="w-4 h-4" /> Retour
          </button>
        </div>
      )}
    </div>
  );
}

export default Step_Welcome;  // re-export helper to unclutter
export { Step_Consent, Step_Rejected, Step_AgeRange, Step_OutOfRange, Step_Already12m, Bool3Row, MultiCheck, TILE, TILE_ACTIVE, SECTION_EMOJI };
